import axios from "axios";
import { apiOrigin } from "./apiBase";

/**
 * Authenticated CRM API client. Reuses the admin JWT stored under
 * `cleany_admin_token` in localStorage by adminApi.js — CRM lives behind
 * the same login, so no separate token flow is needed.
 */
const crm = axios.create({
    baseURL: `${apiOrigin}/api/crm`,
    headers: { "Content-Type": "application/json" },
});

crm.interceptors.request.use((cfg) => {
    const token = localStorage.getItem("cleany_admin_token");
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

// ─── Leads ──────────────────────────────────────────────
export const listLeads = (params = {}) => crm.get("/leads", { params }).then((r) => r.data);
export const createLead = (payload) => crm.post("/leads", payload).then((r) => r.data);
export const getLead = (id) => crm.get(`/leads/${id}`).then((r) => r.data);
export const patchLead = (id, payload) => crm.patch(`/leads/${id}`, payload).then((r) => r.data);
export const deleteLead = (id) => crm.delete(`/leads/${id}`).then((r) => r.data);
export const moveStage = (id, stage) => crm.post(`/leads/${id}/stage`, { stage }).then((r) => r.data);
export const markReplied = (id) => crm.post(`/leads/${id}/mark-replied`).then((r) => r.data);
export const sendTemplateToLead = (id, template_id) =>
    crm.post(`/leads/${id}/send-template`, { template_id }).then((r) => r.data);
export const bulkMoveStage = (ids, stage) =>
    crm.post(`/leads/bulk-stage`, { ids, stage }).then((r) => r.data);
export const bulkSendTemplate = (ids, template_id) =>
    crm.post(`/leads/bulk-send-template`, { ids, template_id }).then((r) => r.data);
export const addNote = (id, payload) => crm.post(`/leads/${id}/note`, typeof payload === "string" ? { text: payload } : payload).then((r) => r.data);
export const listFollowups = (params = {}) => crm.get("/followups", { params }).then((r) => r.data);
export const bookMeeting = (id, meeting) => crm.post(`/leads/${id}/meeting`, meeting).then((r) => r.data);
export const markWon = (id, won_value_dkk) => crm.post(`/leads/${id}/won`, { won_value_dkk }).then((r) => r.data);
export const markLost = (id, payload) => crm.post(`/leads/${id}/lost`, payload).then((r) => r.data);
export const updateNote = (id, activityId, payload) => crm.patch(`/leads/${id}/note/${activityId}`, payload).then((r) => r.data);
export const exportLeads = async (body) => {
    const r = await crm.post("/leads/export", body, { responseType: "blob" });
    const filename = r.headers["x-export-filename"] || `cleany-leads.${body.format === "csv" ? "csv" : "xlsx"}`;
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    return filename;
};
export const listActivities = (id) => crm.get(`/leads/${id}/activities`).then((r) => r.data);

// ─── Mail templates (replaces old flow endpoints) ───────
export const listTemplates = () => crm.get("/templates").then((r) => r.data);
export const createTemplate = (payload) => crm.post("/templates", payload).then((r) => r.data);
export const updateTemplate = (id, payload) => crm.patch(`/templates/${id}`, payload).then((r) => r.data);
export const deleteTemplate = (id) => crm.delete(`/templates/${id}`).then((r) => r.data);
export const reorderTemplates = (order) => crm.post("/templates/reorder", { order }).then((r) => r.data);
export const sendTestTemplate = (id, to) => crm.post(`/templates/${id}/test-email`, { to }).then((r) => r.data);

// ─── Settings ───────────────────────────────────────────
export const getSettings = () => crm.get("/settings").then((r) => r.data);
export const putSettings = (payload) => crm.put("/settings", payload).then((r) => r.data);

// ─── Meta ───────────────────────────────────────────────
export const getStages = () => crm.get("/stages").then((r) => r.data.stages);
export const getLostReasons = () => crm.get("/lost-reasons").then((r) => r.data.reasons);
export const getStats = () => crm.get("/stats").then((r) => r.data);

// ─── Stage management (super_admin only for writes) ─────
export const listStagesManage = () => crm.get("/stages/manage").then((r) => r.data);
export const listStagesMetrics = () => crm.get("/stages/metrics").then((r) => r.data);
export const createStage = (payload) => crm.post("/stages", payload).then((r) => r.data);
export const updateStageMeta = (id, payload) => crm.patch(`/stages/${id}`, payload).then((r) => r.data);
export const deleteStage = (id) => crm.delete(`/stages/${id}`).then((r) => r.data);
export const reorderStages = (order) => crm.post("/stages/reorder", { order }).then((r) => r.data);

// ─── Team + digest preview ──────────────────────────────
export const listAdminUsersLight = () => crm.get("/users/list").then((r) => r.data);
export const getDigestPreview = () => crm.get("/digest/preview").then((r) => r.data);
